import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
    Box,
    Paper,
    Typography,
    Skeleton,
    Chip,
    Tooltip,
    InputBase,
    Select,
    MenuItem,
    LinearProgress,
    Fade,
    alpha
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams, GridFilterModel, GridSortModel } from "@mui/x-data-grid";
import { LineChart } from "@mui/x-charts/LineChart";
import { MessageBar, MessageBarType, IContextualMenuItem } from "@fluentui/react";
import {
    CheckmarkCircle24Regular,
    Clock24Regular,
    ArrowTrendingLines24Regular,
    DocumentBulletList24Regular,
    Info16Regular,
    Warning24Regular,
    People24Regular,
    Flow24Regular,
    BranchFork24Regular,
    Timer24Regular,
    Flag24Regular,
    CalendarLtr24Regular,
    Sparkle24Regular,
    Search16Regular,
    Dismiss16Regular,
    ArrowSortDown16Regular,
    ArrowClockwise16Regular
} from "@fluentui/react-icons";
import { useGetPrioritizeAndDecideQuery } from "../../../slices/CloseTheLoop.slice";
import {
    IPrioritizeAndDecideRequest,
    IBacklogAlignmentFeatureRequest,
    IDeliveryPipelineStage,
    IActiveEngineeringFeature
} from "../../../interfaces/CloseTheLoop.interfaces";
import { logPageView } from "../../../helper/AppInsightsHelper";
import { CustomDataGridToolbar } from "../../../SharedComponents/CustomDataGrid/CustomDataGridToolbar";
import { CustomFilterPill } from "../../../SharedComponents/CustomFilterPill";
import FluentDonutChart from "../../../ChartComponents/FluentDountChart";
import { CUSTOMER_VIEW_STYLES } from "../../CustomerView/CustomerView.styles";
import { IFilterPillStyles } from "@m365-admin/in-page-filter";
import { CTLExploreDataButton } from "../utils/CTLExploreDataButton";
import { CTL_PRIORITIZE_DECIDE_QUERY, generatePrioritizeDecideQuery } from "../utils/ctlKustoQueries";
import { CTLInfoTooltip } from "../utils/CTLInfoTooltip";

// Security 360 Theme - Soft, professional colors (aligned with IntakeToTriage)
const THEME = {
    primary: "#0078D4",
    primaryLight: "#E6F2FA",
    primaryMedium: "#CCE4F7",
    primaryDark: "#005A9E",

    background: "#F8FAFC",
    surface: "#FFFFFF",
    cardBg: "#FAFBFC",

    textPrimary: "#1A1A2E",
    textSecondary: "#64748B",
    textMuted: "#94A3B8",

    border: "#E2E8F0",
    borderLight: "#F1F5F9",

    success: "#059669",
    successLight: "#D1FAE5",
    successMedium: "#A7F3D0",

    warning: "#D97706",
    warningLight: "#FEF3C7",
    warningMedium: "#FDE68A",

    error: "#DC2626",
    errorLight: "#FEE2E2",
    errorMedium: "#FECACA",

    purple: "#7C3AED",
    purpleLight: "#EDE9FE",
    purpleMedium: "#DDD6FE"
};

const FILTER_PILL_STYLES: Partial<IFilterPillStyles> = {
    root: {
        margin: "4px",
        flex: "0 1 auto",
        minWidth: 0
    },
    subComponentStyles: {
        contextualMenu: {
            container: { height: "auto", transition: "min-width 1s ease-in-out" },
            list: { maxHeight: "calc(80vh / 2)", overflow: "auto", "& .ms-ContextualMenu-link": { height: "auto" } }
        },
        callout: { calloutMain: {} }
    }
};

const mapToMenuItems = (options: string[]): IContextualMenuItem[] =>
    options.map((value) => ({ key: value, text: value }));

// Priority chip colors
const PRIORITY_COLORS: Record<string, { bg: string; text: string }> = {
    P0: { bg: "#FDE7E9", text: "#C4314B" },
    P1: { bg: "#FEF5E7", text: "#B7791F" },
    P2: { bg: "#F1F5F9", text: "#64748B" },
    P3: { bg: "#F3F2F1", text: "#605E5C" }
};

// Ordered delivery pipeline stages (left -> right lifecycle flow)
const PIPELINE_STAGE_ORDER = [
    "Needs more info",
    "Pending Triage",
    "Evidence Gathering",
    "In Engineering Backlog",
    "In Development",
    "In Preview Release",
    "Generally Available"
];

const PIPELINE_STAGE_COLORS: Record<string, string> = {
    "Needs more info": "#D97706",
    "Pending Triage": "#F59E0B",
    "Evidence Gathering": "#0078D4",
    "In Engineering Backlog": "#3B82F6",
    "In Development": "#2563EB",
    "In Preview Release": "#10B981",
    "Generally Available": "#059669"
};

/**
 * Summary Card - large number KPI with left accent (mirrors IntakeToTriage design)
 */
interface SummaryCardProps {
    title: string;
    value: number | string;
    subtitle?: string;
    accentColor: string;
    icon?: React.ReactNode;
    isLoading?: boolean;
    /** Business-friendly explanation of the metric, rendered as an info icon next to the title */
    tooltip?: React.ReactNode;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, subtitle, accentColor, icon, isLoading, tooltip }) => {
    if (isLoading) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    border: `1px solid ${THEME.border}`,
                    background: `linear-gradient(135deg, ${THEME.surface} 0%, ${THEME.cardBg} 100%)`,
                    height: 132,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center"
                }}
            >
                <Skeleton variant="text" width={80} height={18} sx={{ borderRadius: "4px" }} />
                <Skeleton variant="text" width={60} height={40} sx={{ borderRadius: "6px" }} />
                <Skeleton variant="text" width={140} height={14} sx={{ borderRadius: "4px" }} />
            </Paper>
        );
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                pl: 3,
                borderRadius: "12px",
                border: `1px solid ${THEME.border}`,
                background: `linear-gradient(135deg, ${THEME.surface} 0%, ${alpha(accentColor, 0.03)} 100%)`,
                height: 132,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                cursor: "default",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                    transform: "translateY(-2px)",
                    borderColor: alpha(accentColor, 0.5),
                    background: `linear-gradient(135deg, ${THEME.surface} 0%, ${alpha(accentColor, 0.08)} 100%)`,
                    boxShadow: `0 8px 24px ${alpha(accentColor, 0.15)}, 0 4px 8px ${alpha(accentColor, 0.1)}`,
                    "& .summary-value": { color: accentColor },
                    "&::before": { width: "5px", boxShadow: `2px 0 12px ${alpha(accentColor, 0.4)}` }
                },
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    bottom: 0,
                    width: "4px",
                    backgroundColor: accentColor,
                    borderRadius: "4px 0 0 4px",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                }
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography
                        sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: THEME.textSecondary,
                            letterSpacing: "0.3px",
                            textTransform: "uppercase"
                        }}
                    >
                        {title}
                    </Typography>
                    {tooltip}
                </Box>
                {icon && (
                    <Box
                        sx={{
                            p: 0.5,
                            borderRadius: "8px",
                            backgroundColor: alpha(accentColor, 0.1),
                            display: "flex"
                        }}
                    >
                        {icon}
                    </Box>
                )}
            </Box>
            <Typography
                className="summary-value"
                sx={{
                    fontSize: "34px",
                    fontWeight: 700,
                    color: THEME.textPrimary,
                    lineHeight: 1,
                    mb: 0.75,
                    letterSpacing: "-0.5px",
                    transition: "color 0.3s ease"
                }}
            >
                {typeof value === "number" ? value.toLocaleString() : value}
            </Typography>
            {subtitle && (
                <Typography
                    sx={{
                        fontSize: "11px",
                        color: THEME.textMuted,
                        fontWeight: 500,
                        letterSpacing: "0.1px"
                    }}
                >
                    {subtitle}
                </Typography>
            )}
        </Paper>
    );
};

/**
 * Section Card wrapper with header (icon + title + subtitle + optional action)
 */
interface SectionCardProps {
    title: string;
    subtitle?: string;
    icon: React.ReactNode;
    action?: React.ReactNode;
    children: React.ReactNode;
    padded?: boolean;
    kustoQuery?: string;
    /** Business-friendly explanation of the visual, rendered as an info icon next to the title */
    tooltip?: React.ReactNode;
}

const SectionCard: React.FC<SectionCardProps> = ({ title, subtitle, icon, action, children, padded = true, kustoQuery, tooltip }) => (
    <Paper
        elevation={0}
        sx={{
            borderRadius: "12px",
            border: `1px solid ${THEME.border}`,
            backgroundColor: THEME.surface,
            overflow: "hidden"
        }}
    >
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: 2.5,
                py: 2,
                borderBottom: `1px solid ${THEME.borderLight}`,
                backgroundColor: THEME.cardBg
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box
                    sx={{
                        p: 0.75,
                        borderRadius: "8px",
                        backgroundColor: THEME.primaryLight,
                        display: "flex"
                    }}
                >
                    {icon}
                </Box>
                <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Typography sx={{ fontSize: "15px", fontWeight: 700, color: THEME.textPrimary }}>{title}</Typography>
                        {tooltip}
                    </Box>
                    {subtitle && <Typography sx={{ fontSize: "12px", color: THEME.textMuted, mt: 0.25 }}>{subtitle}</Typography>}
                </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {action}
            </Box>
        </Box>
        <Box sx={{ p: padded ? 2.5 : 0 }}>{children}</Box>
        {kustoQuery && (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    minHeight: 45,
                    borderTop: `2px solid ${THEME.borderLight}`,
                    px: 1.5
                }}
            >
                <CTLExploreDataButton
                    query={kustoQuery}
                    chartTitle={title}
                />
            </Box>
        )}
    </Paper>
);

/**
 * Toggle button group (e.g. Unmapped Only / All Requests)
 */
interface ToggleButtonsProps {
    options: { key: string; label: string; icon?: React.ReactNode }[];
    value: string;
    onChange: (key: string) => void;
}

const ToggleButtons: React.FC<ToggleButtonsProps> = ({ options, value, onChange }) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            p: 0.5,
            borderRadius: "8px",
            backgroundColor: THEME.background,
            border: `1px solid ${THEME.border}`
        }}
    >
        {options.map((opt) => {
            const selected = opt.key === value;
            return (
                <Box
                    key={opt.key}
                    component="button"
                    onClick={() => onChange(opt.key)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.5,
                        py: 0.75,
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 600,
                        transition: "all 0.2s ease",
                        backgroundColor: selected ? THEME.primary : "transparent",
                        color: selected ? "#FFFFFF" : THEME.textSecondary,
                        boxShadow: selected ? `0 2px 6px ${alpha(THEME.primary, 0.3)}` : "none",
                        "&:hover": {
                            backgroundColor: selected ? THEME.primaryDark : alpha(THEME.primary, 0.08),
                            color: selected ? "#FFFFFF" : THEME.primary
                        }
                    }}
                >
                    {opt.icon}
                    {opt.label}
                </Box>
            );
        })}
    </Box>
);

/**
 * Top Feature Requests ranked horizontal bar list
 */
interface TopFeatureRequestsListProps {
    data: {
        featureRequestId: string;
        featureRequestIdWithSource: string;
        featureRequestTitle: string;
        productFamily: string;
        customerCount: number;
    }[];
    isLoading: boolean;
    onOpenDetails?: (featureRequestIdWithSource: string) => void;
}

const TopFeatureRequestsList: React.FC<TopFeatureRequestsListProps> = ({ data, isLoading, onOpenDetails }) => {
    const top = data.slice(0, 10);
    const maxCount = Math.max(...top.map((d) => d.customerCount), 1);

    // Medal colors for the top 3 ranks; softer neutral for the rest
    const rankStyle = (index: number) => {
        if (index === 0) return { bg: "linear-gradient(135deg, #F6B93B 0%, #E8890C 100%)", color: "#FFFFFF", ring: "#F6B93B" };
        if (index === 1) return { bg: "linear-gradient(135deg, #B8C2CC 0%, #8A97A5 100%)", color: "#FFFFFF", ring: "#B8C2CC" };
        if (index === 2) return { bg: "linear-gradient(135deg, #D3956B 0%, #B4713F 100%)", color: "#FFFFFF", ring: "#D3956B" };
        return { bg: THEME.primaryLight, color: THEME.primaryDark, ring: THEME.primaryMedium };
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={58} sx={{ borderRadius: "12px" }} />
                ))}
            </Box>
        );
    }

    if (top.length === 0) {
        return (
            <Typography sx={{ fontSize: "13px", color: THEME.textMuted, textAlign: "center", py: 3 }}>
                No feature requests available.
            </Typography>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {top.map((item, index) => {
                const rank = rankStyle(index);
                const pctOfTop = Math.round((item.customerCount / maxCount) * 100);
                const isTop3 = index < 3;
                return (
                    <Box
                        key={`${item.featureRequestId}-${index}`}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.75,
                            px: 1.75,
                            py: 1.25,
                            borderRadius: "12px",
                            border: `1px solid ${isTop3 ? alpha(rank.ring, 0.35) : THEME.borderLight}`,
                            background: isTop3
                                ? `linear-gradient(100deg, ${alpha(rank.ring, 0.07)} 0%, ${THEME.surface} 55%)`
                                : THEME.surface,
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                                transform: "translateX(3px)",
                                borderColor: alpha(THEME.primary, 0.4),
                                boxShadow: `0 6px 18px ${alpha(THEME.primary, 0.1)}`
                            }
                        }}
                    >
                        {/* Rank medal */}
                        <Box
                            sx={{
                                width: 30,
                                height: 30,
                                flexShrink: 0,
                                borderRadius: "50%",
                                background: rank.bg,
                                color: rank.color,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "13px",
                                fontWeight: 700,
                                boxShadow: isTop3 ? `0 2px 8px ${alpha(rank.ring, 0.45)}` : "none"
                            }}
                        >
                            {index + 1}
                        </Box>

                        {/* ID + Title + Product */}
                        <Box sx={{ minWidth: 0, flex: "0 0 42%", display: "flex", flexDirection: "column", gap: 0.35 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <Typography
                                    component={onOpenDetails ? "button" : "span"}
                                    onClick={
                                        onOpenDetails
                                            ? () => onOpenDetails(item.featureRequestIdWithSource || item.featureRequestId)
                                            : undefined
                                    }
                                    sx={{
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        color: THEME.primary,
                                        fontFamily: "monospace",
                                        flexShrink: 0,
                                        ...(onOpenDetails
                                            ? {
                                                background: "none",
                                                border: "none",
                                                p: 0,
                                                cursor: "pointer",
                                                "&:hover": { textDecoration: "underline" }
                                            }
                                            : {})
                                    }}
                                >
                                    {item.featureRequestId}
                                </Typography>
                                {item.productFamily && (
                                    <Chip
                                        label={item.productFamily}
                                        size="small"
                                        sx={{
                                            backgroundColor: THEME.primaryLight,
                                            color: THEME.primaryDark,
                                            fontWeight: 600,
                                            fontSize: "9.5px",
                                            height: 18,
                                            borderRadius: "5px"
                                        }}
                                    />
                                )}
                            </Box>
                            <Tooltip title={item.featureRequestTitle || ""} arrow>
                                <Typography
                                    sx={{
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        color: THEME.textPrimary,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    {item.featureRequestTitle}
                                </Typography>
                            </Tooltip>
                        </Box>

                        {/* Demand bar */}
                        <Box sx={{ flex: 1, minWidth: 60, position: "relative" }}>
                            <Box
                                sx={{
                                    position: "relative",
                                    height: 12,
                                    borderRadius: "8px",
                                    backgroundColor: alpha(THEME.primary, 0.08),
                                    overflow: "hidden"
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        width: `${pctOfTop}%`,
                                        borderRadius: "8px",
                                        background: isTop3
                                            ? `linear-gradient(90deg, ${THEME.primaryDark} 0%, ${THEME.primary} 100%)`
                                            : `linear-gradient(90deg, ${alpha(THEME.primary, 0.7)} 0%, ${THEME.primary} 100%)`,
                                        transition: "width 0.7s cubic-bezier(0.4, 0, 0.2, 1)"
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Customer count badge */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                flexShrink: 0,
                                px: 1,
                                py: 0.5,
                                borderRadius: "8px",
                                minWidth: 72,
                                justifyContent: "flex-end",
                                backgroundColor: alpha(THEME.primary, 0.06)
                            }}
                        >
                            <People24Regular style={{ color: THEME.primary, fontSize: 15 }} />
                            <Typography sx={{ fontSize: "15px", fontWeight: 700, color: THEME.textPrimary, lineHeight: 1 }}>
                                {item.customerCount.toLocaleString()}
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

/**
 * Active Feature Requests in Engineering for 90+ Days - rich list with aging bars.
 * Replaces a plain DataGrid with a scannable, severity-aware layout.
 */
interface ActiveEngineeringListProps {
    data: IActiveEngineeringFeature[];
    isLoading: boolean;
    onOpenDetails?: (featureRequestIdWithSource: string) => void;
}

const ActiveEngineeringList: React.FC<ActiveEngineeringListProps> = ({ data, isLoading, onOpenDetails }) => {
    const [search, setSearch] = useState<string>("");
    const [statusFilter, setStatusFilter] = useState<string>("all");
    const [sortBy, setSortBy] = useState<string>("days-desc");

    // Severity buckets based on aging (soft, eye-friendly tones)
    const severityOf = (days: number) => {
        if (days >= 150) return { color: "#E06C75", bg: "#FDECEE", label: "Critical" };
        if (days >= 120) return { color: "#E8975A", bg: "#FDF1E7", label: "At Risk" };
        return { color: "#D9A441", bg: "#FBF4E4", label: "Aging" };
    };

    // Distinct statuses for the filter dropdown
    const statuses = useMemo(() => {
        const set = new Set<string>();
        data.forEach((d) => {
            if (d.internalStatus) set.add(d.internalStatus);
        });
        return Array.from(set).sort();
    }, [data]);

    // Apply search + status filter, then sort
    const processed = useMemo(() => {
        const term = search.trim().toLowerCase();
        let rows = data.filter((d) => {
            const matchesStatus = statusFilter === "all" || d.internalStatus === statusFilter;
            const matchesSearch =
                !term ||
                d.featureRequestId?.toLowerCase().includes(term) ||
                d.featureRequestTitle?.toLowerCase().includes(term) ||
                d.productFamily?.toLowerCase().includes(term);
            return matchesStatus && matchesSearch;
        });

        rows = [...rows].sort((a, b) => {
            switch (sortBy) {
                case "days-asc":
                    return a.daysInDevelopment - b.daysInDevelopment;
                case "id-asc":
                    return (a.featureRequestId || "").localeCompare(b.featureRequestId || "");
                case "id-desc":
                    return (b.featureRequestId || "").localeCompare(a.featureRequestId || "");
                case "days-desc":
                default:
                    return b.daysInDevelopment - a.daysInDevelopment;
            }
        });
        return rows;
    }, [data, search, statusFilter, sortBy]);

    const maxDays = Math.max(...data.map((d) => d.daysInDevelopment), 1);
    const hasActiveFilters = search.trim().length > 0 || statusFilter !== "all";

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={64} sx={{ borderRadius: "10px" }} />
                ))}
            </Box>
        );
    }

    // Shared field styling for the toolbar controls
    const fieldSx = {
        height: 34,
        borderRadius: "8px",
        border: `1px solid ${THEME.border}`,
        backgroundColor: THEME.surface,
        px: 1,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        transition: "border-color 0.2s ease",
        "&:hover": { borderColor: alpha(THEME.primary, 0.4) },
        "&:focus-within": { borderColor: THEME.primary, boxShadow: `0 0 0 3px ${alpha(THEME.primary, 0.1)}` }
    };

    return (
        <Box>
            {/* Toolbar: search + status filter + sort */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                    flexWrap: "wrap"
                }}
            >
                {/* Search */}
                <Box sx={{ ...fieldSx, flex: 1, minWidth: 200, maxWidth: 340 }}>
                    <Search16Regular style={{ color: THEME.textMuted, flexShrink: 0 }} />
                    <InputBase
                        placeholder="Search by ID, title or product…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ fontSize: "13px", flex: 1, "& input": { p: 0 } }}
                    />
                    {search && (
                        <Box
                            component="button"
                            onClick={() => setSearch("")}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                p: 0.25,
                                borderRadius: "4px",
                                "&:hover": { backgroundColor: THEME.borderLight }
                            }}
                        >
                            <Dismiss16Regular style={{ color: THEME.textMuted }} />
                        </Box>
                    )}
                </Box>

                {/* Status filter */}
                <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    size="small"
                    sx={{
                        height: 34,
                        minWidth: 150,
                        fontSize: "13px",
                        borderRadius: "8px",
                        backgroundColor: THEME.surface,
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: THEME.border },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: alpha(THEME.primary, 0.4) },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: THEME.primary }
                    }}
                    renderValue={(v) => (
                        <Typography sx={{ fontSize: "13px", color: THEME.textPrimary }}>
                            {v === "all" ? "All Statuses" : v}
                        </Typography>
                    )}
                >
                    <MenuItem value="all" sx={{ fontSize: "13px" }}>
                        All Statuses
                    </MenuItem>
                    {statuses.map((s) => (
                        <MenuItem key={s} value={s} sx={{ fontSize: "13px" }}>
                            {s}
                        </MenuItem>
                    ))}
                </Select>

                {/* Sort */}
                <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    size="small"
                    startAdornment={<ArrowSortDown16Regular style={{ color: THEME.textMuted, marginRight: 6 }} />}
                    sx={{
                        height: 34,
                        minWidth: 170,
                        fontSize: "13px",
                        borderRadius: "8px",
                        backgroundColor: THEME.surface,
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: THEME.border },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: alpha(THEME.primary, 0.4) },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: THEME.primary }
                    }}
                >
                    <MenuItem value="days-desc" sx={{ fontSize: "13px" }}>
                        Days: High → Low
                    </MenuItem>
                    <MenuItem value="days-asc" sx={{ fontSize: "13px" }}>
                        Days: Low → High
                    </MenuItem>
                    <MenuItem value="id-asc" sx={{ fontSize: "13px" }}>
                        Request ID: A → Z
                    </MenuItem>
                    <MenuItem value="id-desc" sx={{ fontSize: "13px" }}>
                        Request ID: Z → A
                    </MenuItem>
                </Select>

                {/* Result count / clear */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                    <Typography sx={{ fontSize: "12px", color: THEME.textMuted, fontWeight: 500 }}>
                        {processed.length} of {data.length}
                    </Typography>
                    {hasActiveFilters && (
                        <Box
                            component="button"
                            onClick={() => {
                                setSearch("");
                                setStatusFilter("all");
                            }}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 1,
                                py: 0.5,
                                border: `1px solid ${alpha(THEME.error, 0.3)}`,
                                borderRadius: "6px",
                                backgroundColor: alpha(THEME.error, 0.05),
                                color: THEME.error,
                                fontSize: "12px",
                                fontWeight: 500,
                                cursor: "pointer",
                                "&:hover": { backgroundColor: alpha(THEME.error, 0.1) }
                            }}
                        >
                            Clear
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Results */}
            {processed.length === 0 ? (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        py: 5,
                        color: THEME.textMuted
                    }}
                >
                    <CheckmarkCircle24Regular style={{ color: THEME.success, fontSize: 28 }} />
                    <Typography sx={{ fontSize: "13px", color: THEME.textSecondary, fontWeight: 500 }}>
                        {data.length === 0
                            ? "No features have been in engineering for 90+ days."
                            : "No features match the current search or filter."}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {processed.map((item, index) => {
                        const sev = severityOf(item.daysInDevelopment);
                        const pct = Math.min((item.daysInDevelopment / maxDays) * 100, 100);
                        return (
                            <Box
                                key={`${item.featureRequestId}-${index}`}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: "10px",
                                    border: `1px solid ${THEME.border}`,
                                    borderLeft: `4px solid ${sev.color}`,
                                    backgroundColor: THEME.surface,
                                    boxShadow: `0 1px 2px ${alpha(sev.color, 0.06)}`,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        boxShadow: `0 6px 18px ${alpha(sev.color, 0.18)}`,
                                        borderColor: alpha(sev.color, 0.5),
                                        transform: "translateX(3px)"
                                    }
                                }}
                            >
                                {/* Rank + ID + Feature */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, flex: 1 }}>
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: "8px",
                                            background: `linear-gradient(135deg, ${sev.color} 0%, ${alpha(sev.color, 0.75)} 100%)`,
                                            color: "#FFFFFF",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: "13px",
                                            fontWeight: 700,
                                            flexShrink: 0,
                                            boxShadow: `0 2px 6px ${alpha(sev.color, 0.35)}`
                                        }}
                                    >
                                        {index + 1}
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Typography
                                                component={onOpenDetails ? "button" : "span"}
                                                onClick={
                                                    onOpenDetails
                                                        ? () => onOpenDetails(item.featureRequestIdWithSource || item.featureRequestId)
                                                        : undefined
                                                }
                                                sx={{
                                                    fontSize: "12px",
                                                    fontWeight: 600,
                                                    color: THEME.primary,
                                                    fontFamily: "monospace",
                                                    ...(onOpenDetails
                                                        ? {
                                                            background: "none",
                                                            border: "none",
                                                            p: 0,
                                                            cursor: "pointer",
                                                            "&:hover": { textDecoration: "underline" }
                                                        }
                                                        : {})
                                                }}
                                            >
                                                {item.featureRequestId}
                                            </Typography>
                                            {item.productFamily && (
                                                <Chip
                                                    label={item.productFamily}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: THEME.primaryLight,
                                                        color: THEME.primaryDark,
                                                        fontWeight: 600,
                                                        fontSize: "10px",
                                                        height: 18,
                                                        borderRadius: "5px"
                                                    }}
                                                />
                                            )}
                                            {/* Severity label pill */}
                                            <Box
                                                sx={{
                                                    display: { xs: "none", md: "inline-flex" },
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    px: 0.75,
                                                    py: 0.125,
                                                    borderRadius: "5px",
                                                    backgroundColor: sev.bg,
                                                    border: `1px solid ${alpha(sev.color, 0.3)}`
                                                }}
                                            >
                                                <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: sev.color }} />
                                                <Typography
                                                    sx={{
                                                        fontSize: "10px",
                                                        fontWeight: 700,
                                                        color: sev.color,
                                                        letterSpacing: "0.3px",
                                                        textTransform: "uppercase"
                                                    }}
                                                >
                                                    {sev.label}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Tooltip title={item.featureRequestTitle || ""} arrow>
                                            <Typography
                                                sx={{
                                                    fontSize: "13px",
                                                    fontWeight: 600,
                                                    color: THEME.textPrimary,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    mt: 0.25
                                                }}
                                            >
                                                {item.featureRequestTitle}
                                            </Typography>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                {/* Status */}
                                <Chip
                                    label={item.internalStatus}
                                    size="small"
                                    sx={{
                                        backgroundColor: THEME.primaryLight,
                                        color: THEME.primaryDark,
                                        fontWeight: 600,
                                        fontSize: "11px",
                                        height: 24,
                                        borderRadius: "6px",
                                        flexShrink: 0,
                                        display: { xs: "none", sm: "inline-flex" }
                                    }}
                                />

                                {/* Aging bar + days badge */}
                                <Box
                                    sx={{ display: "flex", alignItems: "center", gap: 1.5, width: { xs: 90, md: 250 }, flexShrink: 0 }}
                                >
                                    <Box
                                        sx={{
                                            flex: 1,
                                            height: 8,
                                            borderRadius: "4px",
                                            backgroundColor: alpha(sev.color, 0.12),
                                            overflow: "hidden",
                                            display: { xs: "none", md: "block" }
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                height: "100%",
                                                width: `${pct}%`,
                                                borderRadius: "4px",
                                                background: `linear-gradient(90deg, ${alpha(sev.color, 0.6)} 0%, ${sev.color} 100%)`,
                                                transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
                                            }}
                                        />
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            flexShrink: 0,
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: "8px",
                                            backgroundColor: sev.bg,
                                            border: `1px solid ${alpha(sev.color, 0.3)}`
                                        }}
                                    >
                                        <Timer24Regular style={{ color: sev.color, fontSize: 15 }} />
                                        <Typography
                                            sx={{ fontSize: "15px", fontWeight: 700, color: sev.color, minWidth: 42, textAlign: "right" }}
                                        >
                                            {item.daysInDevelopment}d
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

/**
 * Delivery pipeline - Sankey-style ribbon flow across lifecycle stages.
 * Ribbon thickness reflects the volume carried into the next stage; faded pink
 * drops indicate attrition (volume lost between stages).
 */
interface DeliveryPipelineProps {
    data: IDeliveryPipelineStage[];
    isLoading: boolean;
}

const DeliveryPipeline: React.FC<DeliveryPipelineProps> = ({ data, isLoading }) => {
    const total = data.reduce((sum, s) => sum + s.count, 0);
    const byStatus = useMemo(() => {
        const map: Record<string, number> = {};
        data.forEach((s) => {
            map[s.internalStatus] = s.count;
        });
        return map;
    }, [data]);

    // Order known stages first, then append any extras from the data
    const orderedStages = useMemo(() => {
        const known = PIPELINE_STAGE_ORDER.filter((s) => s in byStatus);
        const extras = data.map((d) => d.internalStatus).filter((s) => !PIPELINE_STAGE_ORDER.includes(s));
        return [...known, ...extras];
    }, [byStatus, data]);

    // Geometry + node model for the SVG flow
    const model = useMemo(() => {
        const N = orderedStages.length;
        if (N === 0) return null;

        const LEFT_PAD = 90;
        const RIGHT_PAD = 90;
        const STAGE_GAP = 180;
        const TOP_Y = 105;
        const BOTTOM_Y = 200;
        const MIN_T = 10;
        const MAX_T = 64;
        const VB_W = LEFT_PAD + (N - 1) * STAGE_GAP + RIGHT_PAD;
        const VB_H = 320;
        const DROP_BASE_Y = 300;

        const counts = orderedStages.map((s) => byStatus[s] || 0);
        const maxCount = Math.max(...counts, 1);
        const scaleT = (c: number) => MIN_T + (c / maxCount) * (MAX_T - MIN_T);

        const nodes = orderedStages.map((stage, i) => {
            const count = byStatus[stage] || 0;
            const x = LEFT_PAD + i * STAGE_GAP;
            const y = i % 2 === 0 ? TOP_Y : BOTTOM_Y;
            const t = scaleT(count);
            const color = PIPELINE_STAGE_COLORS[stage] || THEME.primary;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            return { stage, count, x, y, t, color, pct };
        });

        // Build ribbon + attrition segments between consecutive nodes
        const segments = nodes.slice(0, -1).map((a, i) => {
            const b = nodes[i + 1];
            const cxMid = a.x + (b.x - a.x) * 0.5;

            // Sankey band (tapers from a.t to b.t) following alternating baselines
            const ribbon =
                `M ${a.x} ${a.y - a.t / 2} ` +
                `C ${cxMid} ${a.y - a.t / 2}, ${cxMid} ${b.y - b.t / 2}, ${b.x} ${b.y - b.t / 2} ` +
                `L ${b.x} ${b.y + b.t / 2} ` +
                `C ${cxMid} ${b.y + b.t / 2}, ${cxMid} ${a.y + a.t / 2}, ${a.x} ${a.y + a.t / 2} Z`;

            // Attrition drop (faded) where volume decreases into the next stage
            const lost = Math.max(a.count - b.count, 0);
            const lostT = lost > 0 ? scaleT(a.count) - scaleT(b.count) : 0;
            let attrition: string | null = null;
            if (lostT > 4) {
                const startX = a.x + (b.x - a.x) * 0.22;
                const endX = a.x + (b.x - a.x) * 0.9;
                const topStartY = a.y + a.t / 2;
                attrition =
                    `M ${startX} ${topStartY} ` +
                    `C ${cxMid} ${topStartY + 30}, ${cxMid} ${DROP_BASE_Y}, ${endX} ${DROP_BASE_Y} ` +
                    `L ${endX} ${DROP_BASE_Y} ` +
                    `C ${cxMid + 20} ${DROP_BASE_Y}, ${cxMid} ${topStartY + lostT + 20}, ${startX} ${topStartY + Math.min(lostT, 28)} Z`;
            }

            return { key: `${a.stage}->${b.stage}`, ribbon, attrition, aColor: a.color, bColor: b.color };
        });

        return { nodes, segments, VB_W, VB_H, DROP_BASE_Y };
    }, [orderedStages, byStatus, total]);

    if (isLoading) {
        return <Skeleton variant="rectangular" height={300} sx={{ borderRadius: "12px" }} />;
    }

    if (!model) {
        return (
            <Typography sx={{ fontSize: "13px", color: THEME.textMuted, textAlign: "center", py: 3 }}>
                No pipeline data available.
            </Typography>
        );
    }

    const { nodes, segments, VB_W, VB_H } = model;

    return (
        <Box sx={{ width: "100%", overflowX: "auto", pb: 1 }}>
            <Box
                component="svg"
                viewBox={`0 0 ${VB_W} ${VB_H}`}
                preserveAspectRatio="xMidYMid meet"
                sx={{ width: "100%", minWidth: 760, height: "auto", display: "block" }}
            >
                <defs>
                    {segments.map((seg, i) => (
                        <linearGradient key={`grad-${i}`} id={`ctl-ribbon-${i}`} x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor={seg.aColor} stopOpacity={0.65} />
                            <stop offset="100%" stopColor={seg.bColor} stopOpacity={0.65} />
                        </linearGradient>
                    ))}
                    <linearGradient id="ctl-attrition" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#F9A8B4" stopOpacity={0.55} />
                        <stop offset="100%" stopColor="#FBD5DB" stopOpacity={0} />
                    </linearGradient>
                </defs>

                {/* Attrition drops (render first, behind ribbons) */}
                {segments.map(
                    (seg, i) => seg.attrition && <path key={`attr-${i}`} d={seg.attrition} fill="url(#ctl-attrition)" />
                )}

                {/* Sankey ribbons */}
                {segments.map((seg, i) => (
                    <path key={`ribbon-${i}`} d={seg.ribbon} fill={`url(#ctl-ribbon-${i})`} />
                ))}

                {/* Stage nodes + labels */}
                {nodes.map((n) => {
                    const barH = Math.max(n.t, 42);
                    return (
                        <g key={n.stage}>
                            {/* vertical node bar */}
                            <rect x={n.x - 7} y={n.y - barH / 2} width={14} height={barH} rx={7} fill={n.color} />
                            {/* stage name above bar */}
                            <text
                                x={n.x}
                                y={n.y - barH / 2 - 12}
                                textAnchor="middle"
                                style={{ fontSize: 13, fontWeight: 700, fill: THEME.textPrimary }}
                            >
                                {n.stage}
                            </text>
                            {/* count below bar */}
                            <text
                                x={n.x}
                                y={n.y + barH / 2 + 26}
                                textAnchor="middle"
                                style={{ fontSize: 22, fontWeight: 700, fill: n.color }}
                            >
                                {n.count}
                            </text>
                            {/* percentage below count */}
                            <text
                                x={n.x}
                                y={n.y + barH / 2 + 44}
                                textAnchor="middle"
                                style={{ fontSize: 11, fontWeight: 500, fill: THEME.textMuted }}
                            >
                                {n.pct}% of total
                            </text>
                        </g>
                    );
                })}
            </Box>
        </Box>
    );
};

/**
 * Backlog alignment coverage card (Covered / Not covered by ADO)
 */
interface CoverageCardProps {
    label: string;
    count: number;
    total: number;
    percentage: number;
    color: string;
    icon: React.ReactNode;
    description: string;
    isLoading: boolean;
    /** Business-friendly explanation of the metric, rendered as an info icon next to the label */
    tooltip?: React.ReactNode;
}

const CoverageCard: React.FC<CoverageCardProps> = ({
    label,
    count,
    total,
    percentage,
    color,
    icon,
    description,
    isLoading,
    tooltip
}) => {
    if (isLoading) {
        return <Skeleton variant="rectangular" height={150} sx={{ borderRadius: "12px" }} />;
    }
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                borderRadius: "12px",
                border: `1px solid ${THEME.border}`,
                backgroundColor: THEME.surface,
                display: "flex",
                flexDirection: "column",
                gap: 1.5
            }}
        >
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Typography
                            sx={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: THEME.textSecondary,
                                letterSpacing: "0.4px",
                                textTransform: "uppercase"
                            }}
                        >
                            {label}
                        </Typography>
                        {tooltip}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.5 }}>
                        <Typography sx={{ fontSize: "36px", fontWeight: 700, color: color, lineHeight: 1 }}>
                            {percentage}%
                        </Typography>
                        <Typography sx={{ fontSize: "14px", fontWeight: 500, color: THEME.textMuted }}>
                            {count} of {total}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ p: 0.75, borderRadius: "50%", backgroundColor: alpha(color, 0.1), display: "flex" }}>{icon}</Box>
            </Box>
            <Typography sx={{ fontSize: "12px", color: THEME.textSecondary, lineHeight: 1.4 }}>{description}</Typography>
            <Box
                sx={{ height: 8, width: "100%", borderRadius: "4px", backgroundColor: THEME.borderLight, overflow: "hidden" }}
            >
                <Box
                    sx={{
                        height: "100%",
                        width: `${percentage}%`,
                        borderRadius: "4px",
                        backgroundColor: color,
                        transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
                    }}
                />
            </Box>
        </Paper>
    );
};

/**
 * Distribution donut card (priority / source)
 */
interface DistributionDonutProps {
    title: string;
    subtitle: string;
    icon: React.ReactNode;
    data: { label: string; count: number; percentage: number }[];
    colorMap: (label: string, index: number) => string;
    isLoading: boolean;
    kustoQuery?: string;
    /** Business-friendly explanation of the visual, rendered as an info icon next to the title */
    tooltip?: React.ReactNode;
}

const DistributionDonut: React.FC<DistributionDonutProps> = ({ title, subtitle, icon, data, colorMap, isLoading, kustoQuery, tooltip }) => {
    const total = data.reduce((sum, d) => sum + d.count, 0);
    const chartData = useMemo(
        () =>
            data.map((item, index) => ({
                legend: item.label,
                data: item.count,
                color: colorMap(item.label, index)
            })),
        [data, colorMap]
    );

    return (
        <SectionCard title={title} subtitle={subtitle} icon={icon} kustoQuery={kustoQuery} tooltip={tooltip}>
            {isLoading ? (
                <Skeleton variant="rectangular" height={220} sx={{ borderRadius: "12px" }} />
            ) : data.length === 0 ? (
                <Typography sx={{ fontSize: "13px", color: THEME.textMuted, textAlign: "center", py: 4 }}>
                    No data available.
                </Typography>
            ) : (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 220 }}>
                    <FluentDonutChart
                        data={{ chartTitle: title, chartData }}
                        height={200}
                        width={340}
                        valueInsideDonut={total}
                        styles={CUSTOMER_VIEW_STYLES.COMMON_STYLES.CSS_STYLES.donutChart}
                    />
                </Box>
            )}
        </SectionCard>
    );
};

/**
 * Main PrioritizeAndDecide component
 */
interface PrioritizeAndDecideProps {
    /** Opens the shared feature details panel for a given featureRequestIdWithSource */
    onOpenDetails?: (featureRequestIdWithSource: string) => void;
}

const PrioritizeAndDecide: React.FC<PrioritizeAndDecideProps> = ({ onOpenDetails }) => {
    const [selectedProductFamilies, setSelectedProductFamilies] = useState<string[]>([]);
    const [backlogView, setBacklogView] = useState<string>("unmapped");
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "priority", sort: "asc" }]);

    const queryParams = useMemo<IPrioritizeAndDecideRequest | undefined>(() => {
        const params: IPrioritizeAndDecideRequest = {};
        if (selectedProductFamilies.length > 0) {
            params.productFamilies = selectedProductFamilies.join(",");
        }
        return Object.keys(params).length > 0 ? params : undefined;
    }, [selectedProductFamilies]);

    const { data, isLoading, error, isFetching } = useGetPrioritizeAndDecideQuery(queryParams, {
        refetchOnMountOrArgChange: 300,
        refetchOnFocus: false
    });

    useEffect(() => {
        logPageView("Close The Loop - Prioritize & Decide Tab", window.location.href);
    }, []);

    const productFamilySelectedKeys = useMemo(() => {
        if (!selectedProductFamilies || selectedProductFamilies.length === 0) return undefined;
        return selectedProductFamilies.reduce(
            (acc, value) => {
                acc[value] = true;
                return acc;
            },
            {} as { [key: string]: boolean }
        );
    }, [selectedProductFamilies]);

    const productFamilyMenuItems = useMemo(() => mapToMenuItems(data?.productFamilies || []), [data?.productFamilies]);

    const handleProductFamilyChange = useCallback((values: string[] | undefined) => {
        setSelectedProductFamilies(values || []);
    }, []);

    // Backlog alignment table rows (toggle between Unmapped Only / All)
    const backlogRows = useMemo(() => {
        const rows = data?.backlogAlignmentRequests || [];
        if (backlogView === "unmapped") {
            return rows.filter((r) => !r.isMapped);
        }
        return rows;
    }, [data?.backlogAlignmentRequests, backlogView]);

    const backlogColumns: GridColDef[] = useMemo(
        () => [
            {
                field: "featureRequestId",
                headerName: "Request ID",
                width: 120,
                renderCell: (params: GridRenderCellParams) => (
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: THEME.primary, fontFamily: "monospace" }}>
                        {params.value}
                    </Typography>
                )
            },
            {
                field: "featureRequestTitle",
                headerName: "Title",
                flex: 1,
                minWidth: 240,
                renderCell: (params: GridRenderCellParams) => (
                    <Tooltip title={params.value || ""} arrow>
                        <Typography sx={{ fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {params.value}
                        </Typography>
                    </Tooltip>
                )
            },
            {
                field: "productFamily",
                headerName: "Product",
                width: 130,
                renderCell: (params: GridRenderCellParams) =>
                    params.value ? (
                        <Chip
                            label={params.value}
                            size="small"
                            sx={{
                                backgroundColor: THEME.primaryLight,
                                color: THEME.primaryDark,
                                fontWeight: 600,
                                fontSize: "11px",
                                height: 24,
                                borderRadius: "6px"
                            }}
                        />
                    ) : (
                        <Typography sx={{ fontSize: "13px", color: THEME.textMuted }}>—</Typography>
                    )
            },
            {
                field: "priority",
                headerName: "Priority",
                width: 110,
                renderCell: (params: GridRenderCellParams) => {
                    if (!params.value) return <Typography sx={{ fontSize: "13px", color: THEME.textMuted }}>—</Typography>;
                    const colors = PRIORITY_COLORS[params.value] || { bg: THEME.borderLight, text: THEME.textSecondary };
                    return (
                        <Chip
                            label={params.value}
                            size="small"
                            sx={{
                                backgroundColor: colors.bg,
                                color: colors.text,
                                fontWeight: 600,
                                fontSize: "11px",
                                height: 24,
                                borderRadius: "6px"
                            }}
                        />
                    );
                }
            },
            {
                field: "engineeringWorkItemId",
                headerName: "ADO Work Item",
                width: 150,
                renderCell: (params: GridRenderCellParams) => {
                    const row = params.row as IBacklogAlignmentFeatureRequest;
                    if (row.isMapped && params.value) {
                        return (
                            <Tooltip title={row.workitemTitle || ""} arrow>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <CheckmarkCircle24Regular style={{ color: THEME.success, fontSize: 16 }} />
                                    <Typography sx={{ fontSize: "13px", fontWeight: 500, color: THEME.textPrimary }}>
                                        {params.value}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        );
                    }
                    return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Warning24Regular style={{ color: THEME.error, fontSize: 16 }} />
                            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: THEME.error }}>Unmapped</Typography>
                        </Box>
                    );
                }
            },
            {
                field: "workitemTitle",
                headerName: "Epic / Work Item",
                width: 180,
                renderCell: (params: GridRenderCellParams) =>
                    params.value ? (
                        <Tooltip title={params.value} arrow>
                            <Typography sx={{ fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {params.value}
                            </Typography>
                        </Tooltip>
                    ) : (
                        <Typography sx={{ fontSize: "13px", color: THEME.textMuted }}>—</Typography>
                    )
            },
            {
                field: "engineeringOwner",
                headerName: "Engineering Owner",
                width: 170,
                renderCell: (params: GridRenderCellParams) =>
                    params.value ? (
                        <Typography sx={{ fontSize: "13px" }}>{params.value}</Typography>
                    ) : (
                        <Typography sx={{ fontSize: "13px", color: THEME.textMuted }}>—</Typography>
                    )
            }
        ],
        []
    );

    // Requests over months line chart data
    const monthsData = data?.requestsOverMonths || [];
    // Disambiguate month labels with the year when the window spans more than one calendar year,
    // otherwise two same-named months (e.g. two "Jul") collapse onto the same point-axis category
    // and the line wraps back, drawing a spurious diagonal.
    const spansMultipleYears = new Set(monthsData.map((m) => m.yearMonth.split("-")[0])).size > 1;
    const monthLabels = monthsData.map((m) => {
        const [year, month] = m.yearMonth.split("-");
        const date = new Date(Number(year), Number(month) - 1, 1);
        return spansMultipleYears
            ? date.toLocaleString("en-US", { month: "short", year: "2-digit" })
            : date.toLocaleString("en-US", { month: "short" });
    });
    const monthCounts = monthsData.map((m) => m.count);
    const latestMonth = monthsData.length > 0 ? monthsData[monthsData.length - 1] : undefined;
    const priorMonth = monthsData.length > 1 ? monthsData[monthsData.length - 2] : undefined;
    const monthDelta = latestMonth && priorMonth ? latestMonth.count - priorMonth.count : 0;

    // Status breakdown bar chart data
    const statusData = data?.statusBreakdown || [];

    const priorityColorMap = useCallback((label: string) => {
        const map: Record<string, string> = { P0: "#D13438", P1: "#F39C12", P2: "#0078D4", P3: "#5A5A72" };
        return map[label] || THEME.textSecondary;
    }, []);

    const sourcePalette = ["#0078D4", "#059669", "#F39C12", "#7C3AED", "#D13438", "#0EA5E9", "#EC4899"];
    const sourceColorMap = useCallback(
        (_label: string, index: number) => sourcePalette[index % sourcePalette.length],
        []
    );

    if (error) {
        return (
            <MessageBar messageBarType={MessageBarType.error}>
                Failed to load Prioritize &amp; Decide data. Please try again later.
            </MessageBar>
        );
    }

    const timeToDeliver = data?.timeToDeliver;
    const backlogAlignment = data?.backlogAlignment;
    const overview = data?.overview;

    // Rich refresh indicator: shown while re-fetching after a filter change (but not on first load)
    const isRefreshing = isFetching && !isLoading;

    return (
        <Box sx={{ position: "relative", pb: 4 }}>
            {/* Keyframes for the refresh spinner icon */}
            <style>{`@keyframes ctl-pd-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

            {/* Top progress bar shown whenever data is refreshing */}
            <Fade in={isRefreshing} unmountOnExit>
                <LinearProgress
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 20,
                        height: 3,
                        borderRadius: "3px",
                        mb: 1,
                        backgroundColor: alpha(THEME.primary, 0.12),
                        "& .MuiLinearProgress-bar": { backgroundColor: THEME.primary }
                    }}
                />
            </Fade>

            {/* Info Banner */}
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    mb: 2.5,
                    borderRadius: "10px",
                    background: `linear-gradient(135deg, ${THEME.primaryLight} 0%, ${alpha(THEME.primary, 0.05)} 100%)`,
                    border: `1px solid ${THEME.primaryMedium}`
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Info16Regular style={{ color: THEME.primary, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "12px", color: THEME.textPrimary, lineHeight: 1.5 }}>
                        <strong>Prioritize &amp; Decide:</strong> Prioritize feature requests across the full portfolio &mdash;
                        demand, delivery pipeline health, engineering backlog alignment, and time-to-deliver. Filter to a product
                        family for focused decisions.
                    </Typography>
                </Box>
            </Paper>

            {/* Top Filter - Product Family (applies to all sections) */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2.5,
                    p: 1.5,
                    borderRadius: "10px",
                    background: `linear-gradient(135deg, ${THEME.background} 0%, ${alpha(THEME.primary, 0.03)} 100%)`,
                    border: `1px solid ${THEME.border}`
                }}
            >
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: THEME.textPrimary, whiteSpace: "nowrap" }}>
                    Filter by Product:
                </Typography>
                {(data?.productFamilies?.length || 0) > 0 && (
                    <CustomFilterPill
                        defaultValue="All Products"
                        isRemovable={true}
                        name="Product"
                        styles={FILTER_PILL_STYLES}
                        selectedKeys={productFamilySelectedKeys}
                        useGroupedFilters={false}
                        menuProps={{
                            focusZoneProps: { shouldInputLoseFocusOnArrowKey: () => true },
                            items: productFamilyMenuItems
                        }}
                        onApply={handleProductFamilyChange}
                        onCancel={handleProductFamilyChange}
                        onResetFilters={() => handleProductFamilyChange([])}
                        selectAllButton={{
                            show: true,
                            label: "Select All",
                            selectValues: data?.productFamilies || []
                        }}
                    />
                )}
                <Typography sx={{ fontSize: "11px", color: THEME.textMuted, fontStyle: "italic" }}>
                    (Filters all sections)
                </Typography>
                {/* Refreshing indicator pill */}
                <Fade in={isRefreshing} unmountOnExit>
                    <Box
                        sx={{
                            ml: "auto",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: "16px",
                            backgroundColor: THEME.primaryLight,
                            border: `1px solid ${alpha(THEME.primary, 0.2)}`
                        }}
                    >
                        <ArrowClockwise16Regular style={{ color: THEME.primary, animation: "ctl-pd-spin 0.8s linear infinite" }} />
                        <Typography sx={{ fontSize: "12px", fontWeight: 600, color: THEME.primary }}>Updating results…</Typography>
                    </Box>
                </Fade>
            </Box>

            {/* Content sections - subtly dimmed while refreshing so the change is noticeable */}
            <Box
                sx={{
                    transition: "opacity 0.25s ease, filter 0.25s ease",
                    opacity: isRefreshing ? 0.55 : 1,
                    filter: isRefreshing ? "grayscale(0.15)" : "none",
                    pointerEvents: isRefreshing ? "none" : "auto"
                }}
            >
                {/* ===== 1. Overview KPI cards ===== */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
                        gap: 2,
                        mb: 3
                    }}
                >
                    <SummaryCard
                        title="In Active Development"
                        value={overview?.inActiveDevelopment ?? 0}
                        subtitle="Features currently being built by engineering"
                        accentColor={THEME.primary}
                        icon={<Sparkle24Regular style={{ color: THEME.primary, fontSize: 18 }} />}
                        isLoading={isLoading}
                        tooltip={
                            <CTLInfoTooltip
                                title="In Active Development"
                                description="How much customer-requested work engineering has genuinely picked up and is building right now. This is our delivery capacity in motion."
                                howItsCalculated="We count the distinct feature requests whose engineering status currently reads 'In Development'."
                                note="A live snapshot of today — it moves as work starts and finishes."
                            />
                        }
                    />
                    <SummaryCard
                        title="In Backlog (Not Started)"
                        value={overview?.inEngineeringBacklog ?? 0}
                        subtitle="Pending triage or under consideration"
                        accentColor={THEME.warning}
                        icon={<Clock24Regular style={{ color: THEME.warning, fontSize: 18 }} />}
                        isLoading={isLoading}
                        tooltip={
                            <CTLInfoTooltip
                                title="In Backlog (Not Started)"
                                description="Customer demand that has been accepted by engineering but not yet started. This is our commitment queue — the promises we still owe customers."
                                howItsCalculated="We count the distinct feature requests sitting in the engineering backlog, waiting for work to begin."
                                note="Compare this with 'In Active Development' to judge whether the backlog is being worked down or growing."
                            />
                        }
                    />
                    <SummaryCard
                        title="Completed · Last 30 Days"
                        value={overview?.frCompletedInLast30Days ?? 0}
                        subtitle="Features shipped in the past 30 days"
                        accentColor={THEME.success}
                        icon={<CheckmarkCircle24Regular style={{ color: THEME.success, fontSize: 18 }} />}
                        isLoading={isLoading}
                        tooltip={
                            <CTLInfoTooltip
                                title="Completed · Last 30 Days"
                                description="Recent wins we can take back to customers — the customer-requested features that actually shipped in the last month."
                                howItsCalculated="We count the feature requests whose engineering work was marked finished within the last 30 days."
                                note="A rolling window, so this figure refreshes every day."
                            />
                        }
                    />
                    <SummaryCard
                        title="Completed · Last 60 Days"
                        value={overview?.frCompletedInLast60Days ?? 0}
                        subtitle="Features shipped in the past 60 days"
                        accentColor={THEME.success}
                        icon={<ArrowTrendingLines24Regular style={{ color: THEME.success, fontSize: 18 }} />}
                        isLoading={isLoading}
                        tooltip={
                            <CTLInfoTooltip
                                title="Completed · Last 60 Days"
                                description="A slightly wider view of delivery, useful for smoothing out a quiet or unusually busy month."
                                howItsCalculated="We count the feature requests whose engineering work was marked finished within the last 60 days."
                                note="This includes everything in the 30-day figure, so the two overlap by design."
                            />
                        }
                    />
                </Box>

                {/* ===== 2. Top Feature Requests By Customer Count ===== */}
                <Box sx={{ mb: 3 }}>
                    <SectionCard
                        title="Top Feature Requests By Customer Count"
                        subtitle="Ranked by number of customers requesting — focus prioritization where demand is broadest."
                        icon={<People24Regular style={{ color: THEME.primary }} />}
                        tooltip={
                            <CTLInfoTooltip
                                title="Top Feature Requests By Customer Count"
                                description="The requests with the broadest customer demand behind them. Building these satisfies the greatest number of customers for a single piece of engineering effort, so this is the natural place to start prioritising."
                                howItsCalculated="Requests raised by different customers for the same capability are grouped together, we count how many distinct customers asked for each, and rank them highest first."
                                note="Ranked purely on how many customers asked — it does not weight by revenue or account size."
                            />
                        }
                        kustoQuery={`${CTL_PRIORITIZE_DECIDE_QUERY}
| summarize CustomerCount = dcount(FeatureRequestIdWithSource), TotalRecords = count() by FeatureRequestId, FeatureRequestTitle
| sort by CustomerCount desc
| take 50`}
                    >
                        <TopFeatureRequestsList data={data?.topFeatureRequests || []} isLoading={isLoading} onOpenDetails={onOpenDetails} />
                    </SectionCard>
                </Box>

                {/* ===== 3. Delivery pipeline ===== */}
                <Box sx={{ mb: 3 }}>
                    <SectionCard
                        title="Delivery Pipeline"
                        subtitle="Flow of requests across stages. Ribbon thickness reflects volume carried into the next stage; faded drop indicates attrition."
                        icon={<Flow24Regular style={{ color: THEME.primary }} />}
                        tooltip={
                            <CTLInfoTooltip
                                title="Delivery Pipeline"
                                description="The journey a customer request takes from arrival to delivery, and how many requests are sitting at each step. Narrowing ribbons show where requests drop out, and a bulge shows where work is getting stuck."
                                howItsCalculated="Every request is placed at the stage its engineering status puts it in, and we count the requests at each stage. The thickness of the ribbon between two stages reflects the volume carried forward; the faded portion is what did not progress."
                                note="This is a snapshot of where requests sit today, not a record of how they moved over time."
                            />
                        }
                        action={
                            <Chip
                                label={`${(data?.deliveryPipeline || []).reduce((sum, s) => sum + s.count, 0)} total across ${(data?.deliveryPipeline || []).length} stages`}
                                size="small"
                                sx={{
                                    backgroundColor: THEME.primaryLight,
                                    color: THEME.primaryDark,
                                    fontWeight: 600,
                                    fontSize: "11px",
                                    height: 24,
                                    borderRadius: "6px"
                                }}
                            />
                        }
                        kustoQuery={`${CTL_PRIORITIZE_DECIDE_QUERY}
| summarize Count = count() by InternalStatus
| sort by Count desc`}
                    >
                        <DeliveryPipeline data={data?.deliveryPipeline || []} isLoading={isLoading} />
                    </SectionCard>
                </Box>

                {/* ===== 4. Engineering backlog alignment ===== */}
                <Box sx={{ mb: 3 }}>
                    <SectionCard
                        title="Engineering Backlog Alignment"
                        subtitle="How well incoming feature requests map to existing work items in the Azure DevOps engineering backlog."
                        icon={<BranchFork24Regular style={{ color: THEME.primary }} />}
                        tooltip={
                            <CTLInfoTooltip
                                title="Engineering Backlog Alignment"
                                description="Whether the demand we have accepted is actually reflected in engineering's plan of record. A high covered percentage means customer promises are backed by real, tracked work; a high uncovered percentage means we have accepted requests that nobody is planning to build yet."
                                howItsCalculated="We look at the requests sitting in the engineering backlog and check whether each one is linked to an Azure DevOps work item. Covered means a link exists; Not Covered means it does not. The percentage is each group's share of that backlog."
                                note="Uncovered requests are the action list — they are candidates for raising new backlog entries."
                            />
                        }
                        kustoQuery={`${CTL_PRIORITIZE_DECIDE_QUERY}
| extend HasADOMapping = isnotempty(EngineeringWorkItemId)
| summarize 
    CoveredByADO = countif(HasADOMapping),
    Unmapped = countif(not(HasADOMapping)),
    Total = count()`}
                    >
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                            <CoverageCard
                                label="Covered by ADO Backlog"
                                count={backlogAlignment?.coveredByADO ?? 0}
                                total={backlogAlignment?.totalFeatureRequests ?? 0}
                                percentage={backlogAlignment?.coveredPercentage ?? 0}
                                color={THEME.success}
                                icon={<CheckmarkCircle24Regular style={{ color: THEME.success }} />}
                                description="Requests already aligned to an existing epic or work item."
                                isLoading={isLoading}
                                tooltip={
                                    <CTLInfoTooltip
                                        title="Covered by ADO Backlog"
                                        description="The healthy share — accepted customer requests that engineering has already turned into tracked work. These have a real owner and a real plan behind them."
                                        howItsCalculated="Out of all requests in the engineering backlog, we count those that carry a link to an Azure DevOps work item, and show that as a percentage of the backlog."
                                    />
                                }
                            />
                            <CoverageCard
                                label="Not Covered by Backlog"
                                count={backlogAlignment?.notCoveredByADO ?? 0}
                                total={backlogAlignment?.totalFeatureRequests ?? 0}
                                percentage={backlogAlignment?.notCoveredPercentage ?? 0}
                                color={THEME.error}
                                icon={<Warning24Regular style={{ color: THEME.error }} />}
                                description="Requests with no matching ADO work item — candidates for new backlog entries."
                                isLoading={isLoading}
                                tooltip={
                                    <CTLInfoTooltip
                                        title="Not Covered by Backlog"
                                        description="The risk share — requests we have accepted but that engineering has no tracked work for. If a customer asked for a status update on these, we would have nothing concrete to point to."
                                        howItsCalculated="Out of all requests in the engineering backlog, we count those with no link to an Azure DevOps work item, and show that as a percentage of the backlog."
                                        note="Use the table below to review these and decide which need a backlog entry raising."
                                    />
                                }
                            />
                        </Box>
                    </SectionCard>
                </Box>

                {/* ===== 5. Unmapped feature requests (backlog alignment table) ===== */}
                <Box sx={{ mb: 3 }}>
                    <SectionCard
                        title="Engineering Backlog Requests"
                        subtitle="Requests in the engineering backlog — review whether to create new backlog entries for unmapped items."
                        icon={<DocumentBulletList24Regular style={{ color: THEME.primary }} />}
                        tooltip={
                            <CTLInfoTooltip
                                title="Engineering Backlog Requests"
                                description="The working list behind the coverage figures above. It lets you go request by request and decide which unmapped items deserve a backlog entry."
                                howItsCalculated="Each row is one feature request in the engineering backlog, shown with its product, priority, status and the Azure DevOps work item it is linked to (blank where no link exists)."
                                note="Use the 'Unmapped Only' toggle to focus on the requests with no engineering work behind them."
                            />
                        }
                        action={
                            <ToggleButtons
                                value={backlogView}
                                onChange={setBacklogView}
                                options={[
                                    { key: "unmapped", label: "Unmapped Only", icon: <Warning24Regular style={{ fontSize: 14 }} /> },
                                    { key: "all", label: "All Requests", icon: <DocumentBulletList24Regular style={{ fontSize: 14 }} /> }
                                ]}
                            />
                        }
                        padded={false}
                    >
                        <Box
                            sx={{
                                height: 460,
                                "& .MuiDataGrid-root": { border: "none", fontSize: "13px" },
                                "& .MuiDataGrid-columnHeaders": { backgroundColor: THEME.primary, color: "white", borderRadius: 0 },
                                "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600, fontSize: "12px" },
                                "& .MuiDataGrid-cell": { borderBottom: `1px solid ${THEME.borderLight}`, fontSize: "12px", py: 1 },
                                "& .MuiDataGrid-row:hover": { backgroundColor: alpha(THEME.primary, 0.04) },
                                "& .MuiDataGrid-menuIconButton": { color: "white" },
                                "& .MuiDataGrid-sortIcon": { color: "white" },
                                "& .MuiDataGrid-footerContainer": { borderTop: `1px solid ${THEME.border}`, minHeight: 48 },
                                "& .MuiDataGrid-toolbarContainer": {
                                    padding: "8px 12px",
                                    borderBottom: `1px solid ${THEME.borderLight}`
                                }
                            }}
                        >
                            <DataGrid
                                rows={backlogRows.map((row, index) => ({ ...row, id: `${row.featureRequestId}-${index}` }))}
                                columns={backlogColumns}
                                loading={isLoading}
                                filterModel={filterModel}
                                onFilterModelChange={setFilterModel}
                                sortModel={sortModel}
                                onSortModelChange={setSortModel}
                                pageSizeOptions={[25, 50, 100]}
                                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                                slots={{
                                    toolbar: () => (
                                        <CustomDataGridToolbar
                                            filterModel={filterModel}
                                            toolbarConfig={{ disableGridToolbarQuickFilter: false }}
                                            exportCsvButtonProps={{
                                                buttonLabel: "Export CSV",
                                                fileNamePrefix: "backlog-alignment",
                                                tooltip: "Export data to CSV"
                                            }}
                                        />
                                    )
                                }}
                                disableRowSelectionOnClick
                                density="compact"
                            />
                        </Box>
                        {/* Explore Data Button Footer - Matches CustomCard footer style */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                minHeight: 45,
                                borderTop: `2px solid ${THEME.borderLight}`,
                                px: 1.5
                            }}
                        >
                            <CTLExploreDataButton
                                query={`${CTL_PRIORITIZE_DECIDE_QUERY}
| project FeatureRequestId, FeatureRequestIdWithSource, FeatureRequestTitle, ProductName, ProductFamily, Priority, InternalStatus, EngineeringWorkItemId, EngineeringOwner, FeatureRequestCreatedDate`}
                                chartTitle="Engineering Backlog Alignment Table"
                            />
                        </Box>
                    </SectionCard>
                </Box>

                {/* ===== 6. Time to deliver ===== */}
                <Box sx={{ mb: 3 }}>
                    <SectionCard
                        title="Time to Deliver"
                        subtitle="How quickly feature requests move through engineering and out to customers."
                        icon={<Timer24Regular style={{ color: THEME.primary }} />}
                        tooltip={
                            <CTLInfoTooltip
                                title="Time to Deliver"
                                description="How responsive we are once a customer request reaches engineering. These six measures together answer 'if we say yes to a customer, how long before they actually get it?'."
                                howItsCalculated="Each measure uses the dates recorded against the request — when it was raised, when engineering started, and when it shipped — across features that have completed or are currently active."
                                note="Medians are used rather than averages so a handful of very long-running features do not distort the typical picture."
                            />
                        }
                    >
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" },
                                gap: 2,
                                mb: 2.5
                            }}
                        >
                            <SummaryCard
                                title="Median: Active → Delivery"
                                value={`${timeToDeliver?.medianActiveToDeliveryDays ?? 0}d`}
                                subtitle="Median days from start of engineering work to shipped"
                                accentColor={THEME.primary}
                                icon={<Clock24Regular style={{ color: THEME.primary, fontSize: 18 }} />}
                                isLoading={isLoading}
                                tooltip={
                                    <CTLInfoTooltip
                                        title="Median: Active → Delivery"
                                        description="Pure build speed — once engineering rolls up its sleeves, how long a typical feature takes to reach customers. This is the part of the wait we control most directly."
                                        howItsCalculated="For every shipped feature we measure the days between engineering starting and engineering finishing, then take the middle value across them all."
                                        note="Half of shipped features are faster than this, half are slower."
                                    />
                                }
                            />
                            <SummaryCard
                                title="Median: Created → Done"
                                value={`${timeToDeliver?.medianCreatedToDoneDays ?? 0}d`}
                                subtitle="Median end-to-end lifecycle for shipped features"
                                accentColor={THEME.success}
                                icon={<CheckmarkCircle24Regular style={{ color: THEME.success, fontSize: 18 }} />}
                                isLoading={isLoading}
                                tooltip={
                                    <CTLInfoTooltip
                                        title="Median: Created → Done"
                                        description="The wait as the customer experiences it — from the day they raised the request to the day they got the feature. It includes all the queueing and triage time, not just the build."
                                        howItsCalculated="For every shipped feature we measure the days between the request being raised and engineering finishing, then take the middle value across them all."
                                        note="The gap between this and 'Active → Delivery' is the time requests spend waiting before anyone starts building."
                                    />
                                }
                            />
                            <SummaryCard
                                title="P90 Time to Deliver"
                                value={`${timeToDeliver?.p90TimeToDeliverDays ?? 0}d`}
                                subtitle="90% of shipped features delivered within this many days"
                                accentColor={THEME.success}
                                icon={<ArrowTrendingLines24Regular style={{ color: THEME.success, fontSize: 18 }} />}
                                isLoading={isLoading}
                                tooltip={
                                    <CTLInfoTooltip
                                        title="P90 Time to Deliver"
                                        description="The worst-case promise we can safely make. Nine out of ten features arrive within this many days, so it is a far more honest commitment to give a customer than the median."
                                        howItsCalculated="We line up the delivery times of all shipped features from fastest to slowest and read off the point where 90% of them have landed."
                                        note="A big gap between this and the median means delivery times are inconsistent."
                                    />
                                }
                            />
                            <SummaryCard
                                title="Median Age in Engineering"
                                value={`${timeToDeliver?.medianAgeInEngineeringDays ?? 0}d`}
                                subtitle="Median age of features currently in active development"
                                accentColor={THEME.primary}
                                icon={<Sparkle24Regular style={{ color: THEME.primary, fontSize: 18 }} />}
                                isLoading={isLoading}
                                tooltip={
                                    <CTLInfoTooltip
                                        title="Median Age in Engineering"
                                        description="How long the work currently in flight has already been running. It is an early-warning signal — if this creeps up, today's in-progress features are heading for a slow delivery."
                                        howItsCalculated="For every feature still in active development we measure the days since engineering started, then take the middle value."
                                        note="Unlike the delivery measures, this looks at unfinished work, so it grows every day a feature stays open."
                                    />
                                }
                            />
                            <SummaryCard
                                title="Active ≥ 90 Days"
                                value={timeToDeliver?.activeGte90Days ?? 0}
                                subtitle="Active features that have been in engineering for 90+ days"
                                accentColor={THEME.error}
                                icon={<Warning24Regular style={{ color: THEME.error, fontSize: 18 }} />}
                                isLoading={isLoading}
                                tooltip={
                                    <CTLInfoTooltip
                                        title="Active ≥ 90 Days"
                                        description="Long-running work that may be quietly stuck. These are the features most likely to need unblocking, re-scoping, or an honest conversation with the customer about timing."
                                        howItsCalculated="We count the features still in active development where engineering started 90 or more days ago."
                                        note="The list underneath names each one so you can act on it."
                                    />
                                }
                            />
                            <SummaryCard
                                title="Pipeline Balance"
                                value={`${timeToDeliver?.pipelineBalancePercentage ?? 0}%`}
                                subtitle="Ratio of active features to engineering backlog (active ÷ backlog)"
                                accentColor={THEME.warning}
                                icon={<Flow24Regular style={{ color: THEME.warning, fontSize: 18 }} />}
                                isLoading={isLoading}
                                tooltip={
                                    <CTLInfoTooltip
                                        title="Pipeline Balance"
                                        description="How much of our accepted backlog is actually being worked on. A very low figure means promises are piling up faster than we can deliver them; a very high figure means little runway is left in the backlog."
                                        howItsCalculated="We divide the number of features in active development by the number sitting in the engineering backlog and show the result as a percentage."
                                        note="There is no single 'right' number — read it as a trend alongside the backlog size."
                                    />
                                }
                            />
                        </Box>

                        {/* Explore Data Button - below the 6 metric cards */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                minHeight: 45,
                                borderTop: `2px solid ${THEME.borderLight}`,
                                mb: 2,
                                pt: 1
                            }}
                        >
                            <CTLExploreDataButton
                                query={`${CTL_PRIORITIZE_DECIDE_QUERY}
| where isnotempty(EngineeringWorkItemId)
| extend ActiveToDeliveryDays = iif(isnotempty(EngineeringStartDate) and isnotempty(EngineeringEndDate), datetime_diff("day", EngineeringEndDate, EngineeringStartDate), long(null))
| extend CreatedToDoneDays = iif(isnotempty(FeatureRequestCreatedDate) and isnotempty(EngineeringEndDate), datetime_diff("day", EngineeringEndDate, FeatureRequestCreatedDate), long(null))
| extend ActiveAgeDays = iif(isnotempty(EngineeringStartDate) and isempty(EngineeringEndDate), datetime_diff("day", now(), EngineeringStartDate), long(null))
| summarize
    MedianActiveToDeliveryDays = percentile(ActiveToDeliveryDays, 50),
    MedianCreatedToDoneDays = percentile(CreatedToDoneDays, 50),
    P90TimeToDeliverDays = percentile(ActiveToDeliveryDays, 90),
    ActiveGte90Days = countif(ActiveAgeDays >= 90)`}
                                chartTitle="Time to Deliver"
                            />
                        </Box>

                        {/* Active Feature Requests in Engineering for 90+ Days */}
                        <Box
                            sx={{
                                borderRadius: "12px",
                                border: `1px solid ${THEME.border}`,
                                overflow: "hidden"
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    px: 2,
                                    py: 1.5,
                                    backgroundColor: THEME.cardBg,
                                    borderBottom: `1px solid ${THEME.borderLight}`
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                                    <Box
                                        sx={{
                                            p: 0.75,
                                            borderRadius: "8px",
                                            backgroundColor: THEME.primaryLight,
                                            display: "flex"
                                        }}
                                    >
                                        <Timer24Regular style={{ color: THEME.primary }} />
                                    </Box>
                                    <Box>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: THEME.textPrimary }}>
                                                Active Feature Requests in Engineering for 90+ Days
                                            </Typography>
                                            <CTLInfoTooltip
                                                title="Active Feature Requests in Engineering for 90+ Days"
                                                description="The named list behind the 'Active ≥ 90 Days' figure. Each of these customers has been waiting a long time since we started work, so these are the conversations to get ahead of."
                                                howItsCalculated="We list every feature still in active development where engineering started 90 or more days ago, with the longest-running shown first."
                                                note="Long duration does not always mean trouble — large features legitimately take longer — but each one is worth a check."
                                            />
                                        </Box>
                                        <Typography sx={{ fontSize: "12px", color: THEME.textMuted }}>
                                            Features in active engineering for 90 days or more — candidates for unblocking or re-scoping.
                                        </Typography>
                                    </Box>
                                </Box>
                                <Chip
                                    label={`${data?.activeEngineeringFeatures?.length ?? 0} features`}
                                    size="small"
                                    sx={{
                                        backgroundColor: THEME.primaryLight,
                                        color: THEME.primaryDark,
                                        fontWeight: 700,
                                        fontSize: "12px",
                                        height: 26,
                                        borderRadius: "6px"
                                    }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    p: 1.5,
                                    maxHeight: 268,
                                    overflowY: "auto",
                                    backgroundColor: THEME.background,
                                    "&::-webkit-scrollbar": { width: 8 },
                                    "&::-webkit-scrollbar-thumb": { backgroundColor: alpha(THEME.primary, 0.2), borderRadius: 4 },
                                    "&::-webkit-scrollbar-thumb:hover": { backgroundColor: alpha(THEME.primary, 0.35) },
                                    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" }
                                }}
                            >
                                <ActiveEngineeringList data={data?.activeEngineeringFeatures || []} isLoading={isLoading} onOpenDetails={onOpenDetails} />
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    minHeight: 45,
                                    borderTop: `2px solid ${THEME.borderLight}`,
                                    px: 1.5
                                }}
                            >
                                <CTLExploreDataButton
                                    query={`${CTL_PRIORITIZE_DECIDE_QUERY}
| where InternalStatus == "In Development" and isnotempty(EngineeringStartDate)
| extend DaysInDevelopment = datetime_diff("day", now(), EngineeringStartDate)
| where DaysInDevelopment >= 90
| project FeatureRequestId, FeatureRequestIdWithSource, FeatureRequestTitle, ProductFamily, ProductName, Priority, InternalStatus, EngineeringOwner, EngineeringWorkItemId, EngineeringStartDate, DaysInDevelopment
| sort by DaysInDevelopment desc`}
                                    chartTitle="Active Feature Requests in Engineering for 90+ Days"
                                />
                            </Box>
                        </Box>
                    </SectionCard>
                </Box>

                {/* ===== 7. Requests over months ===== */}
                <Box sx={{ mb: 3 }}>
                    <SectionCard
                        title="Requests Over Months"
                        subtitle="New feature requests by month — trailing 12 months."
                        icon={<CalendarLtr24Regular style={{ color: THEME.primary }} />}
                        tooltip={
                            <CTLInfoTooltip
                                title="Requests Over Months"
                                description="The demand trend for the portfolio — is customer appetite growing, flat, or cooling off? A sustained rise is a signal to argue for more engineering investment."
                                howItsCalculated="We group feature requests by the month they were raised and count the distinct requests in each month, plotting the last 12 months in order."
                                note="The chip in the header compares the latest month with the one before it."
                            />
                        }
                        kustoQuery={`${CTL_PRIORITIZE_DECIDE_QUERY}
| where isnotempty(FeatureRequestCreatedDate)
| extend YearMonth = format_datetime(FeatureRequestCreatedDate, "yyyy-MM")
| summarize Count = dcount(FeatureRequestIdWithSource) by YearMonth
| sort by YearMonth asc`}
                        action={
                            latestMonth ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography sx={{ fontSize: "13px", color: THEME.textSecondary }}>
                                        Latest <strong style={{ color: THEME.textPrimary }}>{monthLabels[monthLabels.length - 1]}</strong>:{" "}
                                        {latestMonth.count}
                                    </Typography>
                                    {priorMonth && (
                                        <Chip
                                            label={`${monthDelta >= 0 ? "+" : ""}${monthDelta} vs. prior month`}
                                            size="small"
                                            sx={{
                                                backgroundColor: monthDelta >= 0 ? THEME.successLight : THEME.errorLight,
                                                color: monthDelta >= 0 ? THEME.success : THEME.error,
                                                fontWeight: 600,
                                                fontSize: "11px",
                                                height: 22,
                                                borderRadius: "6px"
                                            }}
                                        />
                                    )}
                                </Box>
                            ) : undefined
                        }
                    >
                        {isLoading ? (
                            <Skeleton variant="rectangular" height={260} sx={{ borderRadius: "12px" }} />
                        ) : monthsData.length === 0 ? (
                            <Typography sx={{ fontSize: "13px", color: THEME.textMuted, textAlign: "center", py: 4 }}>
                                No monthly data available.
                            </Typography>
                        ) : (
                            <Box sx={{ width: "100%", height: 280 }}>
                                <LineChart
                                    xAxis={[{ scaleType: "point", data: monthLabels }]}
                                    series={[
                                        {
                                            data: monthCounts,
                                            color: THEME.primary,
                                            area: true,
                                            showMark: true,
                                            curve: "monotoneX"
                                        }
                                    ]}
                                    height={280}
                                    margin={{ left: 50, right: 20, top: 20, bottom: 30 }}
                                    sx={{
                                        "& .MuiAreaElement-root": { fill: alpha(THEME.primary, 0.1) },
                                        "& .MuiLineElement-root": { strokeWidth: 2.5 }
                                    }}
                                />
                            </Box>
                        )}
                    </SectionCard>
                </Box>

                {/* ===== 8. Distributions: Priority / Status / Source ===== */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" },
                        gap: 2
                    }}
                >
                    <DistributionDonut
                        title="Requests by Priority"
                        subtitle="Distribution of open feature requests across priority tiers."
                        icon={<Flag24Regular style={{ color: THEME.primary }} />}
                        data={data?.priorityBreakdown || []}
                        colorMap={priorityColorMap}
                        isLoading={isLoading}
                        tooltip={
                            <CTLInfoTooltip
                                title="Requests by Priority"
                                description="How urgent the open demand is. A ring dominated by P0 and P1 means we are carrying a lot of high-stakes commitments; a P2/P3-heavy ring suggests there is room to be selective."
                                howItsCalculated="Each open request carries a priority assigned at triage. We count the requests in each priority tier and show each tier's share of the total."
                                note="Requests with no priority assigned yet are excluded."
                            />
                        }
                        kustoQuery={`${CTL_PRIORITIZE_DECIDE_QUERY}
| where isnotempty(Priority) and Priority != "Not Available"
| summarize Count = count() by Priority
| sort by Count desc`}
                    />

                    <SectionCard
                        title="Status of Feature Requests"
                        subtitle="Where requests currently sit in the workflow."
                        icon={<Flow24Regular style={{ color: THEME.primary }} />}
                        tooltip={
                            <CTLInfoTooltip
                                title="Status of Feature Requests"
                                description="Where the whole request pool currently sits in our workflow. It shows at a glance whether most demand is still awaiting a decision, queued in the backlog, being built, or already closed out."
                                howItsCalculated="Every request is placed in exactly one status based on its current engineering state, and we count the requests in each. Bar length is relative to the largest status."
                                note="A snapshot of today, so it changes as requests move through the workflow."
                            />
                        }
                        kustoQuery={`${CTL_PRIORITIZE_DECIDE_QUERY}
| summarize Count = count() by InternalStatus
| sort by Count desc`}
                    >
                        {isLoading ? (
                            <Skeleton variant="rectangular" height={220} sx={{ borderRadius: "12px" }} />
                        ) : statusData.length === 0 ? (
                            <Typography sx={{ fontSize: "13px", color: THEME.textMuted, textAlign: "center", py: 4 }}>
                                No status data available.
                            </Typography>
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                                {(() => {
                                    const maxStatus = Math.max(...statusData.map((s) => s.count), 1);
                                    return statusData.map((s) => {
                                        const color = PIPELINE_STAGE_COLORS[s.label] || THEME.primary;
                                        const pct = Math.round((s.count / maxStatus) * 100);
                                        return (
                                            <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                                                <Tooltip title={s.label} arrow>
                                                    <Typography
                                                        sx={{
                                                            fontSize: "12px",
                                                            fontWeight: 600,
                                                            color: THEME.textSecondary,
                                                            width: 130,
                                                            flexShrink: 0,
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap"
                                                        }}
                                                    >
                                                        {s.label}
                                                    </Typography>
                                                </Tooltip>
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        height: 14,
                                                        borderRadius: "7px",
                                                        backgroundColor: alpha(color, 0.1),
                                                        overflow: "hidden",
                                                        minWidth: 40
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            height: "100%",
                                                            width: `${pct}%`,
                                                            borderRadius: "7px",
                                                            background: `linear-gradient(90deg, ${alpha(color, 0.75)} 0%, ${color} 100%)`,
                                                            transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
                                                        }}
                                                    />
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: "13px",
                                                        fontWeight: 700,
                                                        color: THEME.textPrimary,
                                                        minWidth: 48,
                                                        textAlign: "right"
                                                    }}
                                                >
                                                    {s.count.toLocaleString()}
                                                </Typography>
                                            </Box>
                                        );
                                    });
                                })()}
                            </Box>
                        )}
                    </SectionCard>

                    <DistributionDonut
                        title="Source Distribution"
                        subtitle="Where feature requests originate."
                        icon={<People24Regular style={{ color: THEME.primary }} />}
                        data={data?.sourceDistribution || []}
                        colorMap={sourceColorMap}
                        isLoading={isLoading}
                        tooltip={
                            <CTLInfoTooltip
                                title="Source Distribution"
                                description="Which channels customer feedback reaches us through. It shows where our listening is strongest and highlights any channel that has gone quiet and may need attention."
                                howItsCalculated="Each request is attributed to the channel it came in through, we count the distinct requests per channel, and show each channel's share of the total."
                                note="A heavy reliance on one channel is a risk — it means we may be missing voices elsewhere."
                            />
                        }
                        kustoQuery={`${CTL_PRIORITIZE_DECIDE_QUERY}
| summarize Count = dcount(FeatureRequestIdWithSource) by Source
| sort by Count desc`}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default PrioritizeAndDecide;
